import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { mapValues } from "@common/helpers/objects";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import {
    Section,
    SectionedCheckboxTable,
} from "@design-system/SectionedCheckboxTable/SectionedCheckboxTable";
import { Picker } from "@design-system/Picker/Picker.lib";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Alert } from "@design-system/Alert/Alert";
import { Field } from "@design-system/Field/Field";
import { shouldShowFieldError } from "@helpers/form";
import { WorkspaceAvatar } from "@design-system/Avatar/WorkspaceAvatar";
import styles from "../../memberManagement/MemberManagement.module.scss";
import {
    OrganizationMemberPermissionsSchema,
    OrganizationTeamFormProps,
} from "./types";
import { useOrganizationRoleOptions } from "./views/useOrganizationRoleOptions";

const fields = createFormikFields<OrganizationMemberPermissionsSchema>();

export const ConfigureOrganizationPermissionsFields: React.FC<
    OrganizationTeamFormProps
> = ({ workspaces }) => {
    const formikContext =
        useFormikContext<OrganizationMemberPermissionsSchema>();

    const selectableOrganizationRoleOptions = useOrganizationRoleOptions();

    const roleMeta =
        formikContext.getFieldMeta<OrganizationUserRole>("organizationRole");
    const selectedOrganizationRole = roleMeta.value;
    const isRoleInvalid = shouldShowFieldError({
        error: roleMeta.error,
        touched: roleMeta.touched,
        submitCount: formikContext.submitCount,
    });

    const preventSelectingIndividualWorkspaces =
        selectedOrganizationRole === OrganizationUserRole.ADMIN;

    const handleOrganizationRoleChange = (role: OrganizationUserRole) => {
        formikContext.setFieldValue("organizationRole", role);
        formikContext.setFieldValue(
            "workspaceAssignments",
            mapValues(
                formikContext.values.workspaceAssignments,
                (selected) => role === OrganizationUserRole.ADMIN || selected,
            ),
        );
        formikContext.setFieldValue(
            "assignToNewClients",
            role === OrganizationUserRole.ADMIN ||
                formikContext.values.assignToNewClients,
        );
    };

    const sections: Array<Section<string>> = useMemo(() => {
        const result: Array<Section<string>> = [];

        if (workspaces.length > 0) {
            const workspaceItems = workspaces.map((workspace) => ({
                value: workspace.id,
                disabled: preventSelectingIndividualWorkspaces,
                render: () => (
                    <>
                        <WorkspaceAvatar
                            workspace={workspace}
                            size="xs"
                            aria-hidden
                        />
                        {workspace.name}
                    </>
                ),
                searchValue: workspace.name ?? undefined,
            }));

            result.push({
                title: "Clients",
                items: workspaceItems,
                allowSelectAll: true,
            });
        }

        return result;
    }, [workspaces, preventSelectingIndividualWorkspaces]);

    const selectedValues = useMemo(() => {
        const result: string[] = [];

        // Add selected workspace IDs
        Object.entries(formikContext.values.workspaceAssignments).forEach(
            ([workspaceId, isSelected]) => {
                if (isSelected) {
                    result.push(workspaceId);
                }
            },
        );

        // Add assignToNewClients if selected
        if (formikContext.values.assignToNewClients) {
            result.push("assignToNewClients");
        }

        return result;
    }, [
        formikContext.values.workspaceAssignments,
        formikContext.values.assignToNewClients,
    ]);

    const handleSelectionChange = (values: string[]) => {
        const newWorkspaceAssignments: Record<string, boolean> = {};
        let newAssignToNewClients = false;

        // Process selected values
        values.forEach((value) => {
            if (value === "assignToNewClients") {
                newAssignToNewClients = true;
            } else {
                newWorkspaceAssignments[value] = true;
            }
        });

        // Ensure all workspaces are represented in workspaceAssignments
        workspaces.forEach((workspace) => {
            if (!(workspace.id in newWorkspaceAssignments)) {
                newWorkspaceAssignments[workspace.id] = false;
            }
        });

        formikContext.setFieldValue(
            "workspaceAssignments",
            newWorkspaceAssignments,
        );
        formikContext.setFieldValue(
            "assignToNewClients",
            newAssignToNewClients,
        );
    };

    return (
        <>
            <section className={styles.roleSection}>
                <header className={styles.sectionHeader}>
                    <h6 className={styles.sectionTitle}>Assign role</h6>
                </header>

                <Field.Root
                    className={styles.roleSelectGroup}
                    invalid={isRoleInvalid}
                >
                    <Picker.Select
                        value={selectedOrganizationRole ?? null}
                        onValueChange={(nextRole) => {
                            if (!nextRole) {
                                return;
                            }

                            handleOrganizationRoleChange(nextRole);
                        }}
                    >
                        <Picker.ButtonTrigger
                            render={
                                <Picker.Button
                                    variant="primary"
                                    size="xl"
                                    aria-label="Assign role"
                                />
                            }
                            value={
                                selectableOrganizationRoleOptions.find(
                                    (option) =>
                                        option.value ===
                                        selectedOrganizationRole,
                                )?.label
                            }
                            placeholder="Select role"
                        />
                        <Picker.Popup>
                            <Picker.List>
                                {selectableOrganizationRoleOptions.map(
                                    (option) => (
                                        <Picker.Item
                                            key={option.value}
                                            value={option.value}
                                            description={option.description}
                                        >
                                            {option.label}
                                        </Picker.Item>
                                    ),
                                )}
                            </Picker.List>
                        </Picker.Popup>
                    </Picker.Select>
                </Field.Root>
            </section>

            {preventSelectingIndividualWorkspaces ? (
                <Alert variant="default" header="All clients included">
                    Admins have full access to all current and future clients in
                    this organization. Individual client selection is not
                    required.
                </Alert>
            ) : (
                <section className={styles.section}>
                    <header className={styles.sectionHeader}>
                        <h6 className={styles.sectionTitle}>Clients</h6>
                    </header>

                    <SectionedCheckboxTable
                        sections={sections}
                        value={selectedValues}
                        onChange={handleSelectionChange}
                        searchable
                        disabled={preventSelectingIndividualWorkspaces}
                        className={styles.entitySelectorContainer}
                    />

                    <div
                        className={styles.assignToNewEntitiesContainer}
                        onClick={(event) => {
                            // The checkbox toggles itself; only handle clicks elsewhere on the row.
                            if (
                                preventSelectingIndividualWorkspaces ||
                                (event.target instanceof Element &&
                                    event.target.closest("[role='checkbox']"))
                            ) {
                                return;
                            }
                            void formikContext.setFieldValue(
                                "assignToNewClients",
                                !formikContext.values.assignToNewClients,
                            );
                        }}
                    >
                        <div>New clients</div>
                        <fields.Checkbox
                            name="assignToNewClients"
                            label="Include new clients"
                            labelHidden
                            className={styles.assignToNewEntitiesField}
                            disabled={preventSelectingIndividualWorkspaces}
                        />
                    </div>
                </section>
            )}
        </>
    );
};
